import { useEffect, useMemo, useRef, useState } from 'react';

import { AdminIcon } from '@/components/admin/icon';
import { useBrands, useCreateBrand } from '@/hooks/use-catalog';
import type { BrandDto } from '@/lib/catalog/catalog-api';

/** Vignette carrée du logo d'une marque (placeholder si aucun logo). */
function BrandLogo({ url, size = 18 }: { url: string | null; size?: number }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 3,
        background: 'var(--a-surface-2, #f6f6f6)',
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
      }}
    >
      {url && (
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      )}
    </span>
  );
}

/**
 * Sélecteur de marque du référentiel (T09-9.2) — mêmes conventions visuelles que
 * `CategoryPicker` : déclencheur `aform-i`, panneau flottant filtrable, création
 * inline. Remplace le champ texte libre. Affiche le logo de la marque.
 */
export function BrandSelect({
  value,
  onChange,
  placeholder = '— Sélectionner une marque —',
  testId,
}: {
  value: string | null;
  onChange: (id: string | null, brand?: BrandDto | null) => void;
  placeholder?: string;
  testId?: string;
}) {
  const { data: brands } = useBrands();
  const createMut = useCreateBrand();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = useMemo<BrandDto | undefined>(
    () => brands?.find((b) => b.id === value),
    [brands, value],
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = query.trim().toLowerCase();
  const matches = useMemo(
    () => (brands ?? []).filter((b) => !q || b.name.toLowerCase().includes(q)).slice(0, 50),
    [brands, q],
  );
  const exact = (brands ?? []).some((b) => b.name.trim().toLowerCase() === q && q.length > 0);

  const pick = (b: BrandDto | null) => {
    onChange(b?.id ?? null, b);
    setOpen(false);
    setQuery('');
  };

  const create = async () => {
    const name = query.trim();
    if (!name) return;
    setErr(null);
    try {
      pick(await createMut.mutateAsync({ name }));
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Création impossible';
      setErr(Array.isArray(msg) ? msg.join(', ') : String(msg));
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }} data-testid={testId}>
      <button
        type="button"
        className="aform-i"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          gap: 8,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
          {selected?.logoUrl && <BrandLogo url={selected.logoUrl} size={18} />}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: selected ? 'inherit' : 'var(--a-muted)',
            }}
          >
            {selected ? selected.name : placeholder}
          </span>
        </span>
        <span
          style={{
            display: 'inline-flex',
            gap: 6,
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {selected && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Retirer la marque"
              title="Retirer la marque"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null, null);
              }}
              style={{ display: 'inline-flex', color: 'var(--a-muted)' }}
            >
              <AdminIcon n="close" s={12} />
            </span>
          )}
          <AdminIcon n="chevD" s={14} c="var(--a-muted)" />
        </span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 50,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            minWidth: 240,
            background: 'var(--a-surface)',
            border: '1px solid var(--a-border)',
            borderRadius: 8,
            boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: 8,
              borderBottom: '1px solid var(--a-border)',
              position: 'relative',
            }}
          >
            <span
              style={{
                position: 'absolute',
                left: 16,
                top: 16,
                color: 'var(--a-muted)',
              }}
            >
              <AdminIcon n="search" s={14} />
            </span>
            <input
              autoFocus
              className="aform-i"
              placeholder="Rechercher ou créer…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !exact && q) {
                  e.preventDefault();
                  void create();
                }
              }}
              style={{ paddingLeft: 32 }}
            />
          </div>
          <div style={{ maxHeight: 300, overflow: 'auto', padding: 6 }}>
            {matches.map((b) => {
              const isSel = b.id === value;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => pick(b)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: isSel ? 'var(--a-surface-2)' : 'transparent',
                    borderRadius: 4,
                    cursor: 'pointer',
                    color: 'inherit',
                    padding: '6px 8px',
                    fontSize: 13,
                    fontWeight: isSel ? 600 : 400,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSel) e.currentTarget.style.background = 'var(--a-surface-2)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSel) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      overflow: 'hidden',
                    }}
                  >
                    <BrandLogo url={b.logoUrl} size={18} />
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {b.name}
                    </span>
                  </span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ color: 'var(--a-muted)', fontSize: 11 }}>{b.productsCount}</span>
                    {isSel && <AdminIcon n="check" s={14} c="var(--a-primary)" />}
                  </span>
                </button>
              );
            })}
            {q && !exact && (
              <button
                type="button"
                onClick={() => void create()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  borderTop: '1px solid var(--a-border)',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--a-primary)',
                  padding: '8px',
                  marginTop: 4,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <AdminIcon n="plus" s={12} c="var(--a-primary)" />
                Créer la marque « {query.trim()} »
              </button>
            )}
            {!matches.length && !q && (
              <div className="muted" style={{ fontSize: 12, padding: '8px 6px' }}>
                Aucune marque
              </div>
            )}
          </div>
        </div>
      )}
      {err && (
        <div style={{ color: 'var(--a-danger, #c00)', fontSize: 11, marginTop: 4 }}>{err}</div>
      )}
    </div>
  );
}
